package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

enum class AppButtonVariant { Primary, Secondary, Tonal, Text, Destructive }

/**
 * The single button component for the whole app. Variants: Primary (solid teal), Secondary
 * (white + neutral border), Tonal (neutral container fill, e.g. list "Details"), Text,
 * Destructive (error fill — irreversible actions like account deletion). Optional
 * [leadingIcon]/[trailingIcon] are Material Symbols ligature
 * names (rendered via [AppIcon]). Handles enabled/disabled/loading. Never hand-roll a button.
 */
@Composable
fun AppButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    variant: AppButtonVariant = AppButtonVariant.Primary,
    enabled: Boolean = true,
    loading: Boolean = false,
    leadingIcon: String? = null,
    trailingIcon: String? = null,
    leadingContent: (@Composable () -> Unit)? = null,
) {
    val shape = AppTheme.shapes.md
    val content: @Composable () -> Unit = {
        Row(
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AnimatedVisibility(loading) {
                CircularProgressIndicator(
                    modifier = Modifier.size(AppTheme.sizes.iconSm),
                    strokeWidth = AppTheme.sizes.strokeSm,
                )
            }
            // Custom slot (e.g. a multicolor brand mark like GoogleLogo) wins over the symbol name.
            when {
                leadingContent != null -> leadingContent()
                leadingIcon != null -> AppIcon(name = leadingIcon, size = AppTheme.sizes.iconButton)
            }
            Text(text = text, style = AppTheme.typography.labelMd)
            if (trailingIcon != null) AppIcon(name = trailingIcon, size = AppTheme.sizes.iconButton)
        }
    }

    when (variant) {
        AppButtonVariant.Primary -> Button(
            onClick = onClick,
            modifier = modifier,
            enabled = enabled && !loading,
            shape = shape,
            colors = ButtonDefaults.buttonColors(
                containerColor = AppTheme.colors.primaryContainer,
                contentColor = AppTheme.colors.onPrimary,
            ),
        ) { content() }

        AppButtonVariant.Secondary -> OutlinedButton(
            onClick = onClick,
            modifier = modifier,
            enabled = enabled && !loading,
            shape = shape,
            border = BorderStroke(AppTheme.sizes.borderWidth, AppTheme.colors.outlineVariant),
            colors = ButtonDefaults.outlinedButtonColors(
                containerColor = AppTheme.colors.surface,
                contentColor = AppTheme.colors.primary,
            ),
        ) { content() }

        AppButtonVariant.Tonal -> Button(
            onClick = onClick,
            modifier = modifier,
            enabled = enabled && !loading,
            shape = shape,
            colors = ButtonDefaults.buttonColors(
                containerColor = AppTheme.colors.surfaceContainer,
                contentColor = AppTheme.colors.onSurfaceVariant,
            ),
        ) { content() }

        AppButtonVariant.Destructive -> Button(
            onClick = onClick,
            modifier = modifier,
            enabled = enabled && !loading,
            shape = shape,
            colors = ButtonDefaults.buttonColors(
                containerColor = AppTheme.colors.error,
                contentColor = AppTheme.colors.onError,
            ),
        ) { content() }

        AppButtonVariant.Text -> TextButton(
            onClick = onClick,
            modifier = modifier,
            enabled = enabled && !loading,
            shape = shape,
            colors = ButtonDefaults.textButtonColors(
                contentColor = AppTheme.colors.primary,
            ),
        ) { content() }
    }
}
